import { useState } from "react";
import { useIntl } from "react-intl";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormHelperText from "@mui/material/FormHelperText";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Messages from "../../../localization/Messages";
import RpisDialog from "../../RpisDialog/RpisDialog";

export enum CancelBookingReason {
    HIGH_WATER = "HIGH_WATER",
    LOW_WATER = "LOW_WATER",
    LOCKS = "LOCKS",
    SCHEDULE_CHANGE = "SCHEDULE_CHANGE",
    OTHER = "OTHER",
}

export interface ICancelBookingDialog {
    open: boolean;
    onClose: () => void;
    onConfirm: (reasonText: string) => void | Promise<void>;
}

/**
 * Dialog shown to a Locks & Docks user before cancelling a booking.
 * Forces selection of one of the predefined cancellation reasons or a
 * custom text when "Other" is chosen. Returns the localized reason string
 * (prefixed with "Cancellation reason: ") to the caller.
 */
export const CancelBookingDialog = ({ open, onClose, onConfirm }: ICancelBookingDialog) => {
    const intl = useIntl();
    const [reason, setReason] = useState<CancelBookingReason | "">("");
    const [otherText, setOtherText] = useState("");
    const [submitted, setSubmitted] = useState(false);

    const resetAndClose = () => {
        setReason("");
        setOtherText("");
        setSubmitted(false);
        onClose();
    };

    const reasonLabel = (r: CancelBookingReason) => {
        switch (r) {
            case CancelBookingReason.HIGH_WATER:
                return intl.formatMessage(Messages.cancelReasonHighWater);
            case CancelBookingReason.LOW_WATER:
                return intl.formatMessage(Messages.cancelReasonLowWater);
            case CancelBookingReason.LOCKS:
                return intl.formatMessage(Messages.cancelReasonLocks);
            case CancelBookingReason.SCHEDULE_CHANGE:
                return intl.formatMessage(Messages.cancelReasonScheduleChange);
            case CancelBookingReason.OTHER:
                return intl.formatMessage(Messages.cancelReasonOther);
        }
    };

    const reasonMissing = reason === "";
    const otherMissing = reason === CancelBookingReason.OTHER && otherText.trim() === "";

    const handleConfirm = async () => {
        setSubmitted(true);
        if (reasonMissing || otherMissing) return;
        const reasonText =
            reason === CancelBookingReason.OTHER
                ? otherText.trim()
                : reasonLabel(reason as CancelBookingReason);
        // Prefix is always in German regardless of the current UI language,
        // because the cancellation remark is appended to the dispatcher remark
        // which is read by German-speaking dispatchers.
        const prefix = "Stornierungsgrund";
        await onConfirm(`${prefix}: ${reasonText}`);
        resetAndClose();
    };

    return (
        <RpisDialog
            dialogOpen={open}
            onClose={resetAndClose}
            title={intl.formatMessage(Messages.cancelBookingDialogTitle)}
            size="sm"
            fullWidth
            content={
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, padding: 2 }}>
                    <Typography variant="body2">
                        {intl.formatMessage(Messages.cancelBookingDialogDescription)}
                    </Typography>
                    <FormControl error={submitted && reasonMissing} required>
                        <RadioGroup
                            value={reason}
                            onChange={e => setReason(e.target.value as CancelBookingReason)}
                        >
                            {Object.values(CancelBookingReason).map(r => (
                                <FormControlLabel
                                    key={r}
                                    value={r}
                                    control={<Radio />}
                                    label={reasonLabel(r)}
                                />
                            ))}
                        </RadioGroup>
                        {submitted && reasonMissing && (
                            <FormHelperText>
                                {intl.formatMessage(Messages.cancelReasonRequired)}
                            </FormHelperText>
                        )}
                    </FormControl>
                    {reason === CancelBookingReason.OTHER && (
                        <TextField
                            autoFocus
                            multiline
                            minRows={2}
                            variant="filled"
                            label={intl.formatMessage(Messages.cancelReasonOtherPlaceholder)}
                            value={otherText}
                            onChange={e => setOtherText(e.target.value)}
                            error={submitted && otherMissing}
                            helperText={
                                submitted && otherMissing
                                    ? intl.formatMessage(Messages.cancelReasonOtherRequired)
                                    : ""
                            }
                            inputProps={{ maxLength: 500 }}
                        />
                    )}
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, marginTop: 1 }}>
                        <Button onClick={resetAndClose} color="inherit">
                            {intl.formatMessage(Messages.cancel)}
                        </Button>
                        <Button
                            onClick={handleConfirm}
                            variant="contained"
                            color="error"
                            disabled={reasonMissing || otherMissing}
                        >
                            {intl.formatMessage(Messages.cancelBooking)}
                        </Button>
                    </Box>
                </Box>
            }
        />
    );
};

export default CancelBookingDialog;
